import React, { useCallback, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { map } from 'lodash-es'
import { useDropzone } from 'react-dropzone'
import { CircularProgress, IconButton, Tooltip } from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import CancelIcon from '@mui/icons-material/Cancel'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import {
  dropzoneAccept,
  formatDropzoneRejectionError,
} from './formatDropzoneRejectionError'

const useStyles = makeStyles(() => ({
  title: {
    paddingBottom: 16,
  },
  inputContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    textAlign: 'center',
    minHeight: 100,
    padding: 18,
    borderWidth: 2,
    borderRadius: 2,
    borderColor: palette.grey.grey300,
    borderStyle: 'dashed',
    backgroundColor: palette.grey.grey100,
    color: palette.grey.grey500,
    outline: 'none',
    transition: 'border .24s ease-in-out',
    '&:hover': {
      borderColor: palette.secondary.purple600,
    },
  },
  fileContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  fileNameContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  imagePreview: {
    width: 100,
  },
}))

/*
  files: [
    {
      name: '',
      downloadUrl: '',
    },
    ....
  ]
*/

type File = {
  name: string
  downloadUrl: string
  type: string
}

interface FileUploadProps {
  placeholder: string
  isMultipleFilesAllowed?: boolean
  disabled?: boolean
  acceptedFilesTypes: string[]
  placeholderSubtitle: string
  maxFileNumber?: number
  maxSize?: number
  onChange: (files: File[]) => void
  files: File[]
  onError?: (error: string) => void
  onUploaded?: (response: any) => void
  uploadFiles: (files: File[]) => void
}

export default ({
  placeholder = 'Drag and drop files here, or click to select files',
  isMultipleFilesAllowed = false,
  disabled = false,
  acceptedFilesTypes, // e.g. ['.png', '.jpeg', '.pdf']
  placeholderSubtitle,
  maxFileNumber = 5,
  maxSize = 10, // in MB,
  onChange = () => null,
  files = [],
  onError = () => null,
  onUploaded = () => null,
  uploadFiles = () => null,
}: FileUploadProps) => {
  const classes = useStyles()
  const [isUploadingActive, setIsUploadingActive] = useState(false)

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        onError(
          formatDropzoneRejectionError(rejectedFiles[0].errors[0], maxSize)
        )
        return
      }
      setIsUploadingActive(true)
      try {
        const response = await uploadFiles(acceptedFiles)
        setIsUploadingActive(false)
        onUploaded(response)
      } catch (error) {
        setIsUploadingActive(false)
        onError(error instanceof Error ? error.message : 'Upload failed')
      }
    },
    [maxSize, onError, onUploaded, uploadFiles]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: isMultipleFilesAllowed,
    disabled,
    accept: dropzoneAccept(acceptedFilesTypes),
    maxFiles: maxFileNumber,
    maxSize: maxSize * 1024 * 1024,
  })

  const removeFileFromList = (file) => {
    const newArray = []
    map(files, (fileHere) => {
      // Delete the file object
      if (fileHere.name !== file.name) {
        newArray.push(fileHere)
      }
    })
    onChange(newArray)
  }

  const componentState = () => {
    if (files.length > 0) {
      return 'UPLOADED'
    }
    if (isUploadingActive) {
      return 'UPLOADING'
    }
    return 'IDLE'
  }

  // Do not structure the component any other way, it cases problems with the dropzone
  return (
    <div>
      {componentState() === 'IDLE' && (
        <div {...getRootProps()} className={classes.inputContainer}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <Text variant='body2'>Drop the files here ...</Text>
          ) : (
            <div>
              <Text variant='body1'>{placeholder}</Text>
              <Text variant='action4'>{placeholderSubtitle}</Text>
            </div>
          )}
        </div>
      )}
      {componentState() === 'UPLOADING' && (
        <div className={classes.inputContainer}>
          <CircularProgress sx={{ color: palette.primary.pink800 }} />
        </div>
      )}
      {componentState() === 'UPLOADED' && (
        <div className={classes.inputContainer}>
          {files.map((file) => (
            <div className={classes.fileContainer}>
              {/* If the file type includes pdf displaying the pdf logo */}
              {file.type.includes('pdf') && (
                <PictureAsPdfIcon fontSize='large' color='primary' />
              )}
              {/* If the file type includes image displaying the image */}
              {!file.type.includes('pdf') && (
                <img
                  className={classes.imagePreview}
                  src={file.downloadUrl}
                  alt={file.name}
                />
              )}
              <div className={classes.fileNameContainer}>
                <Text variant='body2'>{file.name.substring(0, 10)}</Text>
                {!disabled && (
                  <Tooltip title='Remove'>
                    <IconButton onClick={() => removeFileFromList(file)}>
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
